import React, { useState, useRef } from 'react';
import { 
  QrCode, 
  Copy, 
  Share, 
  Download, 
  Mail, 
  MessageSquare, 
  Link, 
  X,
  Check,
  Smartphone,
  Monitor,
  Users,
  Eye,
  EyeOff,
  Wifi,
  Globe
} from 'lucide-react';
import QRCode from 'qrcode.react';

function QRCodeShare({ 
  url, 
  title = "CloudStream Invitation", 
  description = "Join the stream", 
  streamId,
  hostName,
  onClose 
}) {
  const [qrSize, setQrSize] = useState(256);
  const [includeData, setIncludeData] = useState(true);
  const [showUrl, setShowUrl] = useState(false);
  const [copied, setCopied] = useState(false);
  const qrRef = useRef(null);

  const handleCopyUrl = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error('Failed to copy URL:', error);
    }
  };

  const handleDownloadQR = () => {
    const canvas = qrRef.current?.querySelector('canvas');
    if (canvas) {
      const link = document.createElement('a');
      link.download = `cloudstream-invite-${streamId || 'qr'}.png`;
      link.href = canvas.toDataURL();
      link.click();
    }
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: title,
          text: description,
          url: url,
        });
      } catch (error) {
        console.error('Error sharing:', error);
        handleCopyUrl();
      }
    } else {
      handleCopyUrl();
    }
  };

  const handleEmailShare = () => {
    const subject = encodeURIComponent(title);
    const body = encodeURIComponent(
      `${description}\n\nJoin the stream: ${url}\n\nOr scan the QR code to join instantly!`
    );
    window.open(`mailto:?subject=${subject}&body=${body}`, '_blank');
  };

  const handleSMSShare = () => {
    const text = encodeURIComponent(`${description} - ${url}`);
    window.open(`sms:?body=${text}`, '_blank');
  };

  const formatUrl = (url) => {
    return url.length > 50 ? `${url.substring(0, 47)}...` : url;
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-800 rounded-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="p-6 border-b border-gray-700">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-xl font-semibold text-white mb-1">Share Invitation</h3>
              <p className="text-gray-400 text-sm">Let others join your stream easily</p>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-white rounded-lg hover:bg-gray-700 transition-colors duration-200"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="p-6 space-y-6">
          {/* QR Code Display */}
          <div className="text-center">
            <div className="bg-white p-6 rounded-lg inline-block mb-4" ref={qrRef}>
              <QRCode
                value={url}
                size={qrSize}
                level="M"
                includeMargin={true}
                fgColor="#000000"
                bgColor="#ffffff"
              />
            </div>
            
            {includeData && (
              <div className="bg-gray-700 rounded-lg p-4 mb-4">
                <div className="flex items-center justify-center space-x-2 mb-2">
                  <QrCode className="w-5 h-5 text-cf-orange" />
                  <h4 className="text-white font-medium">Stream Details</h4>
                </div>
                <div className="text-sm text-gray-300 space-y-1">
                  {hostName && <p><strong>Host:</strong> {hostName}</p>}
                  {streamId && <p><strong>Stream:</strong> {streamId}</p>}
                  <p><strong>Platform:</strong> CloudStream</p>
                </div>
              </div>
            )}

            <p className="text-gray-400 text-sm">
              Scan with any QR code reader or camera app to join instantly
            </p>
          </div>

          {/* QR Code Options */}
          <div className="space-y-4">
            <h4 className="text-white font-medium">QR Code Options</h4>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-400 mb-2">
                  Size
                </label>
                <select
                  value={qrSize}
                  onChange={(e) => setQrSize(parseInt(e.target.value))}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white text-sm"
                >
                  <option value={128}>Small (128px)</option>
                  <option value={256}>Medium (256px)</option>
                  <option value={512}>Large (512px)</option>
                </select>
              </div>
              
              <div className="flex items-end">
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={includeData}
                    onChange={(e) => setIncludeData(e.target.checked)}
                    className="rounded"
                  />
                  <span className="text-gray-300 text-sm">Show details</span>
                </label>
              </div>
            </div>
          </div>

          {/* Sharing Options */}
          <div className="space-y-4">
            <h4 className="text-white font-medium">Share Options</h4>
            
            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={handleCopyUrl}
                className="flex items-center space-x-2 p-3 bg-gray-700 hover:bg-gray-600 rounded-lg transition-colors duration-200"
              >
                {copied ? (
                  <>
                    <Check className="w-4 h-4 text-green-400" />
                    <span className="text-green-400 text-sm">Copied!</span>
                  </>
                ) : (
                  <>
                    <Copy className="w-4 h-4 text-gray-300" />
                    <span className="text-gray-300 text-sm">Copy Link</span>
                  </>
                )}
              </button>

              <button
                onClick={handleShare}
                className="flex items-center space-x-2 p-3 bg-gray-700 hover:bg-gray-600 rounded-lg transition-colors duration-200"
              >
                <Share className="w-4 h-4 text-gray-300" />
                <span className="text-gray-300 text-sm">Share</span>
              </button>

              <button
                onClick={handleDownloadQR}
                className="flex items-center space-x-2 p-3 bg-gray-700 hover:bg-gray-600 rounded-lg transition-colors duration-200"
              >
                <Download className="w-4 h-4 text-gray-300" />
                <span className="text-gray-300 text-sm">Download QR</span>
              </button>

              <button
                onClick={handleEmailShare}
                className="flex items-center space-x-2 p-3 bg-gray-700 hover:bg-gray-600 rounded-lg transition-colors duration-200"
              >
                <Mail className="w-4 h-4 text-gray-300" />
                <span className="text-gray-300 text-sm">Email</span>
              </button>

              <button
                onClick={handleSMSShare}
                className="flex items-center space-x-2 p-3 bg-gray-700 hover:bg-gray-600 rounded-lg transition-colors duration-200"
              >
                <MessageSquare className="w-4 h-4 text-gray-300" />
                <span className="text-gray-300 text-sm">SMS</span>
              </button>

              <button
                onClick={() => setShowUrl(!showUrl)}
                className="flex items-center space-x-2 p-3 bg-gray-700 hover:bg-gray-600 rounded-lg transition-colors duration-200"
              >
                {showUrl ? (
                  <EyeOff className="w-4 h-4 text-gray-300" />
                ) : (
                  <Eye className="w-4 h-4 text-gray-300" />
                )}
                <span className="text-gray-300 text-sm">
                  {showUrl ? 'Hide URL' : 'Show URL'}
                </span>
              </button>
            </div>
          </div>

          {/* URL Display */}
          {showUrl && (
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-400">
                Invitation URL
              </label>
              <div className="flex items-center space-x-2">
                <input
                  type="text"
                  value={url}
                  readOnly
                  className="flex-1 px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white text-sm font-mono"
                />
                <button
                  onClick={handleCopyUrl}
                  className="p-2 bg-cf-orange hover:bg-orange-600 text-white rounded transition-colors duration-200"
                >
                  {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                </button>
              </div>
            </div>
          )}

          {/* Instructions */}
          <div className="bg-blue-500 bg-opacity-10 border border-blue-500 rounded-lg p-4">
            <div className="flex items-start space-x-3">
              <Smartphone className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
              <div>
                <h5 className="text-blue-400 font-medium mb-2">How to Join</h5>
                <ul className="text-gray-300 text-sm space-y-1">
                  <li className="flex items-center space-x-2">
                    <div className="w-1 h-1 bg-gray-400 rounded-full"></div>
                    <span>Scan QR code with phone camera or QR reader</span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <div className="w-1 h-1 bg-gray-400 rounded-full"></div>
                    <span>Click the invitation link on any device</span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <div className="w-1 h-1 bg-gray-400 rounded-full"></div>
                    <span>Works on mobile, tablet, and desktop</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Device Compatibility */}
          <div className="grid grid-cols-3 gap-4 text-center">
            <div className="p-3 bg-gray-700 rounded-lg">
              <Smartphone className="w-6 h-6 text-green-400 mx-auto mb-2" />
              <p className="text-gray-300 text-xs">Mobile</p>
            </div>
            <div className="p-3 bg-gray-700 rounded-lg">
              <Monitor className="w-6 h-6 text-green-400 mx-auto mb-2" />
              <p className="text-gray-300 text-xs">Desktop</p>
            </div>
            <div className="p-3 bg-gray-700 rounded-lg">
              <Globe className="w-6 h-6 text-green-400 mx-auto mb-2" />
              <p className="text-gray-300 text-xs">Web Browser</p>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-700">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2 text-gray-400 text-xs">
              <Wifi className="w-4 h-4" />
              <span>Secure connection required</span>
            </div>
            <button
              onClick={onClose}
              className="btn-secondary"
            >
              Done
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default QRCodeShare; 